import { ArrowLeft, PenTool, Heart, Sparkles, Clock, BookOpen, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function CreativeWritingGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-blue-700 hover:text-blue-800 hover:bg-blue-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-blue-100 rounded-full">
              <PenTool className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Creative Writing Play</h1>
              <p className="text-blue-600">Week 11, Day 4 - Creative Expression</p>
            </div>
          </div>

          <div className="prose prose-blue max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Set a timer for 20 minutes and write without stopping. Don't edit as you go. Let the words 
              flow freely onto the page. This isn't about perfection - it's about expression.
            </p>

            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-blue-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Benefits of Creative Writing
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Processes and releases emotions</li>
                <li>• Increases self-understanding</li>
                <li>• Provides emotional release and catharsis</li>
                <li>• Improves communication skills</li>
                <li>• Creates space for imagination</li>
                <li>• Can be done anywhere with just pen and paper</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Writing Prompts</h2>
            
            <div className="space-y-3 mb-8">
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
                <p className="text-gray-700 font-medium">Letter to Your Younger Self</p>
                <p className="text-gray-500 text-sm">What would you tell the 20-year-old you? What do you wish you'd known?</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
                <p className="text-gray-700 font-medium">A Memory That Shaped You</p>
                <p className="text-gray-500 text-sm">Describe a pivotal moment in your life in vivid detail.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
                <p className="text-gray-700 font-medium">Your Perfect Day</p>
                <p className="text-gray-500 text-sm">Describe your ideal day from morning to night, no limitations.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
                <p className="text-gray-700 font-medium">Stream of Consciousness</p>
                <p className="text-gray-500 text-sm">Start with "Right now I feel..." and keep writing whatever comes.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
                <p className="text-gray-700 font-medium">Fiction: A Character Unlike You</p>
                <p className="text-gray-500 text-sm">Create a character very different from yourself. What's their story?</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
                <p className="text-gray-700 font-medium">A Poem About [Topic]</p>
                <p className="text-gray-500 text-sm">Try: morning light, your hands, a feeling, a season, a relationship.</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-blue-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                The Rules of Freewriting
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold">1.</span>
                  <span><strong>Don't stop writing</strong> - If you get stuck, write "I don't know what to write" until something comes.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold">2.</span>
                  <span><strong>Don't edit</strong> - No crossing out, no backspace, no fixing spelling. Keep moving forward.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold">3.</span>
                  <span><strong>Don't judge</strong> - Silence your inner critic. There's no good or bad here.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold">4.</span>
                  <span><strong>Follow tangents</strong> - If your mind wanders, follow it. That's often where gold hides.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold">5.</span>
                  <span><strong>Keep it private</strong> - Knowing no one will read it allows complete honesty.</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                After You Write
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Read it over once (or don't - that's okay too)</li>
                <li>• Notice any surprises or insights</li>
                <li>• Observe how you feel after writing</li>
                <li>• Consider keeping it, or ceremonially letting it go</li>
                <li>• No need to share with anyone</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
